import Box from '@material-ui/core/Box';
import classNames from 'classnames';
import { useContextCheckboxWithOptionalContent } from 'components/CheckboxWithOptionalContent';
import { FieldInputProps } from 'formik';
import React, { ChangeEvent, FC, useEffect } from 'react';
import styled from 'styled-components';

export const FormCheck = styled.div`
  &&.form-check {
    padding-left: 0px;
  }
  @media screen and (max-width: 576px) {
    &&.form-check {
      margin-top: 7px;
      margin-bottom: 7px;
    }
  }
`;
export const FormCheckLabel = styled.label`
  &.form-check-label {
    display: flex;
    align-items: center;
    position: relative;
    padding-left: 0px;
    padding-right: 15px;
    font-size: 22px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      &:checked ~ .checkmark {
        background-color: ${(props) => props.theme.colors.white};
        border: 1px solid ${(props) => props.theme.colors.malibu};
        &::after {
          display: block;
          background: ${(props) => props.theme.colors.malibu};
        }
      }
      &:disabled:checked ~ .checkmark {
        background: ${(props) => props.theme.colors.solitude06};
        border: 1px solid ${(props) => props.theme.colors.malibu06};
        &::after {
          top: 4px;
          left: 4px;
          width: 15px;
          height: 15px;
          border-radius: 50%;
          background: ${(props) => props.theme.colors.malibu06};
          border: none;
        }
      }
      &:disabled ~ .checkmark {
        cursor: not-allowed;
        background: ${(props) => props.theme.colors.solitude06};
        border: 1px solid ${(props) => props.theme.colors.ghost06};
      }
      &.is-disabled:disabled ~ .checkmark {
        cursor: not-allowed;
        background: ${(props) => props.theme.colors.solitude06};
        border: 1px solid ${(props) => props.theme.colors.ghost};
        &::after {
          top: 4px;
          left: 4px;
          width: 15px;
          height: 15px;
          border-radius: 50%;
          background: ${(props) => props.theme.colors.lightSlateGrey06};
          border: none;
        }
      }
      &.is-disabled:checked:disabled ~ .checkmark {
        cursor: not-allowed;
        background-color: ${(props) => props.theme.colors.solitude06};
        border: 1px solid ${(props) => props.theme.colors.ghost};
        &::after {
          top: 4px;
          left: 4px;
          width: 15px;
          height: 15px;
          border-radius: 50%;
          background: ${(props) => props.theme.colors.lightSlateGrey06};
          border: none;
        }
      }
    }
    .checkmark {
      position: relative;
      min-height: 25px;
      min-width: 25px;
      border: 1px solid ${(props) => props.theme.colors.ghost};
      background: ${(props) => props.theme.colors.white};
      border-radius: 50%;
      margin-right: 8px;
      &::after {
        content: '';
        position: absolute;
        display: none;
      }
    }
    .checkmark:after {
      position: absolute;
      top: 4px;
      left: 4px;
      width: 15px;
      height: 15px;
      border-radius: 50%;
      border: solid ${(props) => props.theme.colors.malibu};
    }
    input:disabled ~ .checkmark:after {
      background: ${(props) => props.theme.colors.solitude06};
      border: 1px solid ${(props) => props.theme.colors.ghost06};
    }
  }
  span {
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    cursor: pointer;
    color: ${(props) => props.theme.colors.dolphin};
  }
  &.checkmark::after {
    content: '';
    position: absolute;
    display: none;
  }
  input:focus ~ .checkmark {
    border-color: ${(props) => props.theme.colors.malibu};
  }
  input:disabled ~ span {
    cursor: not-allowed;
  }
  &:hover {
    input ~ .checkmark {
      border-color: ${(props) => props.theme.colors.malibu};
      cursor: pointer;
    }
    input:disabled ~ .checkmark {
      border: 1px solid ${(props) => props.theme.colors.ghost};
    }
  }
`;
export const CheckMark = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: 25px;
  width: 25px;
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.ghost};
  &::after {
    content: '';
    position: absolute;
    display: none;
  }
`;
export interface Props {
  key?: string;
  form: any;
  editable: boolean;
  disabled?: boolean;
  label?: string;
  value?: string | number;
  noclear?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  field: FieldInputProps<any>;
  name?: string;
  initialValue?: string | number;
  padding?: number;
  style?: React.CSSProperties;
}
const RadioButtonWithLabel: React.VFC<Props> = ({
  form,
  editable,
  disabled,
  label,
  value,
  initialValue,
  noclear,
  onChange,
  field,
  name,
  style,
}) => {
  const { checked } = useContextCheckboxWithOptionalContent();
  const inputClassName = classNames('form-check-input', {
    'is-disabled': !editable && !disabled,
  });
  useEffect(() => {
    if (checked !== undefined && !checked && form && field.value && !noclear) {
      form.setFieldValue(field.name, undefined);
    }
  }, [checked, field, form, noclear]);

  return (
    <FormCheckLabel style={style} className="form-check-label">
      <input
        type="radio"
        className={inputClassName}
        disabled={!editable || disabled}
        {...field}
        name={name || field.name}
        value={value}
        onChange={onChange || field.onChange}
        checked={initialValue === value || field.value === value}
      />
      <CheckMark className="checkmark"></CheckMark>
      <span>{label}</span>
    </FormCheckLabel>
  );
};

const RadioButtonWithLabelNoFormik: FC<{
  style?: React.CSSProperties;
  editable: boolean;
  label: string;
  value?: string | number;
  selected?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}> = (props) => {
  return (
    <FormCheckLabel style={props.style} className="form-check-label">
      <input
        type="radio"
        disabled={!props.editable}
        value={props.value}
        onChange={props.onChange}
        checked={props.selected}
      />
      <CheckMark className="checkmark"></CheckMark>
      <span>{props.label}</span>
    </FormCheckLabel>
  );
};

const AdaptMgRadioButtonWithLabel: React.VFC<Props> = (props) => {
  return (
    <Box
      sx={{
        py: props?.padding && props?.padding >= 0 ? props?.padding : 1.25,
      }}
    >
      <RadioButtonWithLabel {...props} />
    </Box>
  );
};
export default RadioButtonWithLabel;
export { AdaptMgRadioButtonWithLabel, RadioButtonWithLabelNoFormik };
